import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { Tag } from '../types';
import useSimActions from './useSimActions';

interface UseUpdateProspectTagsAndNotesProps {
  notesInput: string;
  selectedTags: Tag[];
  notes: string;
  tags: Tag[];
  onApplyNotes: () => void;
  onApplyTags: () => void;
  setNotesInput: Dispatch<SetStateAction<string>>;
  setSelectedTags: Dispatch<SetStateAction<Tag[]>>;
  personaId?: string;
}

const useUpdateProspectTagsAndNotes = ({
  notesInput,
  selectedTags,
  notes,
  tags,
  onApplyNotes,
  onApplyTags,
  setNotesInput,
  setSelectedTags,
  personaId,
}: UseUpdateProspectTagsAndNotesProps) => {
  const { runApplyTags, runEdit, isApplyingTags, isEditing: isEditingNotes } = useSimActions();

  const tagIdsToApply = useMemo(
    () => selectedTags.filter((newTag) => !tags.find((oldTag) => oldTag.id === newTag.id)).map((tag) => tag.id),
    [selectedTags, tags]
  );

  const tagIdsToRemove = useMemo(
    () => tags.filter((oldTag) => !selectedTags.find((newTag) => newTag.id === oldTag.id)).map((tag) => tag.id),
    [tags, selectedTags]
  );

  const runApplyTagsAndNotes = useCallback(async () => {
    // If there is no personaId, we are not updating an existing prospect,
    // so we can immediately call the onSuccess callback.
    if (!personaId) {
      onApplyTags();
      onApplyNotes();
      return;
    }

    // We handle updating tags and notes separately because they are two different API calls.
    await runApplyTags({
      id: personaId,
      tagsToApply: tagIdsToApply,
      tagsToRemove: tagIdsToRemove,
      onError: () => setSelectedTags(tags),
      onSuccess: onApplyTags,
    });

    if (notes !== notesInput) {
      await runEdit({
        payload: { id: personaId, notes: notesInput },
        onError: () => setNotesInput(notes),
        onSuccess: onApplyNotes,
      });
    }
  }, [
    personaId,
    notes,
    notesInput,
    tags,
    tagIdsToApply,
    tagIdsToRemove,
    runApplyTags,
    runEdit,
    onApplyTags,
    onApplyNotes,
    setSelectedTags,
    setNotesInput,
  ]);

  return { runApplyTagsAndNotes, isLoading: isApplyingTags || isEditingNotes };
};

export default useUpdateProspectTagsAndNotes;
